import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import logo from "../../assets/images/visaro-logo-avatar.png";
import { CheckCircle, X, User, Buildings, ArrowRight } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as yup from "yup";
import useRegister from "../../hooks/useRegister";
import Navbar from "../../components/Navbar";
import { useAuth } from "../../context/AuthContext";
import { HashLoader } from "react-spinners";
import { isAlreadyVerifiedEmailResponse } from "../../utils/onboardingEmailResponse";
import {
  inferNextStepFromUser,
  pathForNextStep,
} from "../../utils/onboardingNextStep";
import { toast } from "sonner";
import {
  clearPendingReferralCode,
  getPendingReferralCodeFromStorage,
  isValidReferralCode,
  normalizeReferralCodeInput,
  setPendingReferralCode,
} from "../../utils/referralCode";
import {
  acquisitionSignupFields,
  captureAcquisitionFromParams,
  clearPendingAcquisition,
  getPendingAcquisitionFromStorage,
  PendingAcquisition,
} from "../../utils/acquisitionSource";

const schema = yup.object().shape({
  email: yup
    .string()
    .email("Please enter a valid email address")
    .required("Email is required"),
  manualReferral: yup
    .string()
    .optional()
    .test(
      "referral-format",
      "Use 6–32 letters or numbers only",
      (v) => {
        if (v === undefined || v === null || String(v).trim() === "")
          return true;
        const n = normalizeReferralCodeInput(v);
        return n !== null && isValidReferralCode(n);
      }
    ),
});

const SelectAccount = () => {
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [referralFromUrl, setReferralFromUrl] = useState<string | null>(null);
  const [showReferralExtra, setShowReferralExtra] = useState(false);
  const [acquisition, setAcquisition] = useState<PendingAcquisition | null>(
    () => getPendingAcquisitionFromStorage()
  );

  const { register, registerError, registering, isRegisterError } = useRegister();

  // Organization link parameters, kept separate from the referral code above so that arriving
  // through a partner's link and typing a friend's code are not mutually exclusive.
  useEffect(() => {
    setAcquisition(captureAcquisitionFromParams(searchParams));
  }, [searchParams]);

  useEffect(() => {
    const raw = searchParams.get("ref") || searchParams.get("referral_code");
    const n = normalizeReferralCodeInput(raw);
    if (n && isValidReferralCode(n)) {
      setReferralFromUrl(n);
      setPendingReferralCode(n);
      setShowReferralExtra(false);
      return;
    }
    if (raw !== null && String(raw).trim() !== "") {
      toast.error(
        "That referral link looks invalid. Continue without a code or enter one below."
      );
    }
  }, [searchParams]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, []);

  const { user, isFetchingUser } = useAuth();

  useEffect(() => {
    if (user && !isFetchingUser) {
      // Password is now the last onboarding step, so a password-less user is a
      // normal mid-flow state — route via the funnel, not straight to password.
      const step = inferNextStepFromUser(user);
      const path = pathForNextStep(step);
      if (path) navigate(path, { replace: true });
      else navigate("/home", { replace: true });
    }
  }, [user, isFetchingUser, navigate]);

  return isFetchingUser ? (
    <div className="h-screen w-screen flex items-center justify-center">
      <HashLoader color="#EA5C29" />
    </div>
  ) : (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      
      <div className="flex-1 flex flex-col items-center justify-center p-6 md:p-12 lg:p-20">
        
        <div className="text-center max-w-4xl mb-12">
          <span className="text-visaro-orange font-bold uppercase tracking-wider text-sm mb-2 block">
            Get Started
          </span>
          <h1 className="text-deepBlue text-4xl md:text-6xl font-bold font-aeonic mb-4 leading-tight">
            How do you want to use Visaro?
          </h1>
          <p className="text-gray-500 text-lg md:text-xl max-w-2xl mx-auto">
            Choose the type of account that best fits your financial needs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full max-w-5xl">
          
          <motion.div
            whileHover={{ y: -8 }}
            transition={{ type: "spring", stiffness: 300 }}
            onClick={() => setShowModal(true)}
            className="group cursor-pointer bg-white p-8 md:p-10 rounded-3xl border border-gray-200 shadow-sm hover:shadow-xl hover:border-visaro-orange/50 transition-all duration-300 relative overflow-hidden flex flex-col h-full"
          >
            <div className="absolute top-0 right-0 -mt-8 -mr-8 w-32 h-32 bg-visaro-orange/5 rounded-full group-hover:scale-150 transition-transform duration-500" />
            
            <div className="flex justify-between items-start mb-6 relative z-10">
              <div>
                <h2 className="text-3xl font-bold text-deepBlue mb-2">Personal</h2>
                <div className="h-1 w-12 bg-visaro-orange rounded-full"></div>
              </div>
              <div className="bg-orange-50 p-3 rounded-2xl text-visaro-orange">
                <User size={32} weight="duotone" />
              </div>
            </div>

            <p className="text-gray-600 text-lg mb-8 leading-relaxed">
              Send, spend, and manage your money. All from one app.
            </p>

            <ul className="space-y-4 mb-8">
              {[
                "Send and receive money instantly",
                "Buy now pay later on products",
                "Spread payments easily"
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <CheckCircle size={24} weight="fill" className="text-visaro-orange flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600 font-medium">{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-auto pt-6 border-t border-gray-100">
               <span className="text-deepBlue font-bold group-hover:text-visaro-orange transition-colors flex items-center gap-2 text-lg">
                 Create Personal Account <ArrowRight weight="bold" />
               </span>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ y: -8 }}
            transition={{ type: "spring", stiffness: 300 }}
            onClick={() => window.location.replace("https://merchant.visaro.ng")}
            className="group cursor-pointer bg-white p-8 md:p-10 rounded-3xl border border-gray-200 shadow-sm hover:shadow-xl hover:border-visaro-orange/50 transition-all duration-300 relative overflow-hidden flex flex-col h-full"
          >
            <div className="absolute top-0 right-0 -mt-8 -mr-8 w-32 h-32 bg-blue-50 rounded-full group-hover:scale-150 transition-transform duration-500" />
            
            <div className="flex justify-between items-start mb-6 relative z-10">
              <div>
                <h2 className="text-3xl font-bold text-deepBlue mb-2">Business</h2>
                <div className="h-1 w-12 bg-visaro-orange rounded-full"></div>
              </div>
              <div className="bg-blue-50 p-3 rounded-2xl text-deepBlue">
                <Buildings size={32} weight="duotone" />
              </div>
            </div>

            <p className="text-gray-600 text-lg mb-8 leading-relaxed">
              Get paid online, in-store, or on the go. Help grow your business.
            </p>

            <ul className="space-y-4 mb-8">
              {[
                "Accept multiple forms of payment",
                "Integrate with business tools",
                "Access ready-to-buy user base"
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <CheckCircle size={24} weight="fill" className="text-visaro-orange flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600 font-medium">{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-auto pt-6 border-t border-gray-100">
               <span className="text-deepBlue font-bold group-hover:text-visaro-orange transition-colors flex items-center gap-2 text-lg">
                 Create Business Account <ArrowRight weight="bold" />
               </span>
            </div>
          </motion.div>

        </div>
      </div>

      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed top-0 left-0 bg-black/60 backdrop-blur-md w-full h-screen grid place-items-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="w-full max-w-md"
            >
              <Formik
                validationSchema={schema}
                enableReinitialize
                initialValues={{
                  email: "",
                  manualReferral: getPendingReferralCodeFromStorage() ?? "",
                }}
                onSubmit={(values, { resetForm }) => {
                  const manual = normalizeReferralCodeInput(values.manualReferral);
                  const fromManual =
                    manual && isValidReferralCode(manual) ? manual : null;
                  const fromStored = getPendingReferralCodeFromStorage();
                  const code =
                    referralFromUrl ??
                    fromManual ??
                    (fromStored && isValidReferralCode(fromStored) ? fromStored : null);

                  const payload: {
                    email: string;
                    referral_code?: string;
                    acquisition_code?: string;
                    acquisition_click_id?: string;
                  } = {
                    email: values.email.trim(),
                    ...acquisitionSignupFields(
                      acquisition ?? getPendingAcquisitionFromStorage()
                    ),
                  };
                  if (code && isValidReferralCode(code)) {
                    payload.referral_code = code;
                    setPendingReferralCode(code);
                  }

                  register(payload, {
                    onSuccess(data) {
                      // Core already persisted attribution or skipped an existing account.
                      clearPendingAcquisition();
                      setAcquisition(null);
                      if (isAlreadyVerifiedEmailResponse(data)) {
                        const nextStep = (data?.data as any)?.nextStep;

                        // Email verified but password never set — resume the registration
                        if (nextStep === "profile_creation") {
                          navigate("resume-profile-otp", {
                            state: { email: values.email.trim() },
                          });
                          resetForm();
                          setShowModal(false);
                          return;
                        }

                        // Fully registered — send to login
                        navigate("/login", {
                          state: {
                            infoMessage:
                              "This email is already registered. Sign in to continue.",
                          },
                        });
                        resetForm();
                        setShowModal(false);
                        return;
                      }
                      navigate(`email-otp`, {
                        state: {
                          email: payload.email,
                          ...(payload.referral_code
                            ? { referral_code: payload.referral_code }
                            : {}),
                        },
                      });
                      resetForm();
                    },
                  });
                }}
              >
                {({ isValid, dirty, values, setFieldValue }) => (
                  <Form className="bg-white p-8 rounded-3xl shadow-2xl flex flex-col gap-6 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-visaro-orange to-deepBlue" />
                    
                    <div className="flex justify-between items-center">
                       <div className="w-10"></div> 
                       <img src={logo} alt="Visaro" className="h-12 w-auto" />
                       <button
                        type="button"
                        onClick={() => setShowModal(false)}
                        className="p-2 bg-gray-50 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                      >
                        <X size={20} weight="bold" />
                      </button>
                    </div>

                    <div className="text-center">
                      <h3 className="text-2xl font-bold text-deepBlue mb-2">Welcome to Visaro</h3>
                      <p className="text-gray-500 text-sm">
                        Enter your email address to begin your personal banking journey.
                      </p>
                    </div>

                    {referralFromUrl && (
                      <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                        <p className="font-medium">You&apos;re signing up with a referral</p>
                        <p className="font-mono mt-1 text-emerald-950">{referralFromUrl}</p>
                        <button
                          type="button"
                          className="text-visaro-orange font-semibold mt-2 hover:underline text-sm"
                          onClick={() => {
                            setReferralFromUrl(null);
                            clearPendingReferralCode();
                            navigate({ pathname: "/sign-up", search: "" }, { replace: true });
                            setShowReferralExtra(true);
                          }}
                        >
                          Change referral code
                        </button>
                      </div>
                    )}

                    <div className="flex flex-col gap-2">
                      <label htmlFor="email" className="text-sm font-bold text-gray-700 ml-1">
                        Email Address
                      </label>
                      <Field
                        type="email"
                        id="email"
                        name="email"
                        placeholder="name@example.com"
                        className="rounded-xl border border-gray-300 bg-gray-50 focus:bg-white focus:border-visaro-orange focus:ring-4 focus:ring-visaro-orange/10 transition-all px-4 py-3.5 w-full outline-none"
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="text-red-500 text-sm ml-1 mt-1"
                      />
                    </div>

                    {!referralFromUrl && (
                      <div className="flex flex-col gap-2">
                        {!showReferralExtra ? (
                          <button
                            type="button"
                            className="text-left text-sm font-semibold text-visaro-orange hover:underline"
                            onClick={() => setShowReferralExtra(true)}
                          >
                            Have a referral code?
                          </button>
                        ) : (
                          <>
                            <label
                              htmlFor="manualReferral"
                              className="text-sm font-bold text-gray-700 ml-1"
                            >
                              Referral code (optional)
                            </label>
                            <Field
                              type="text"
                              id="manualReferral"
                              name="manualReferral"
                              autoComplete="off"
                              placeholder="e.g. ABC12345"
                              className="rounded-xl border border-gray-300 bg-gray-50 focus:bg-white focus:border-visaro-orange focus:ring-4 focus:ring-visaro-orange/10 transition-all px-4 py-3.5 w-full outline-none font-mono uppercase"
                            />
                            <ErrorMessage
                              name="manualReferral"
                              component="div"
                              className="text-red-500 text-sm ml-1 mt-1"
                            />
                          </>
                        )}
                      </div>
                    )}

                    {isRegisterError && (
                      <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm text-center border border-red-100 space-y-2">
                        <p>{registerError?.message}</p>
                        {(values.manualReferral || referralFromUrl) && (
                          <button
                            type="button"
                            className="text-deepBlue font-semibold underline"
                            onClick={() => {
                              clearPendingReferralCode();
                              setReferralFromUrl(null);
                              setFieldValue("manualReferral", "");
                              navigate({ pathname: "/sign-up", search: "" }, { replace: true });
                              setShowReferralExtra(false);
                            }}
                          >
                            Try again without a referral code
                          </button>
                        )}
                      </div>
                    )}

                    <div className="flex flex-col gap-4 mt-2">
                      <button
                        disabled={registering || !isValid || !dirty}
                        type="submit"
                        className="p-4 w-full bg-deepBlue hover:bg-visaro-orange text-white rounded-xl font-bold text-lg shadow-lg shadow-deepBlue/20 hover:shadow-visaro-orange/30 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {registering ? <HashLoader size={20} color="white" /> : "Continue with Email"}
                      </button>

                      <div className="relative flex py-2 items-center">
                        <div className="flex-grow border-t border-gray-200"></div>
                        <span className="flex-shrink-0 mx-4 text-gray-400 text-xs uppercase font-bold tracking-wider">or</span>
                        <div className="flex-grow border-t border-gray-200"></div>
                      </div>

                      <Link
                        to="/login"
                        className="p-4 w-full border-2 border-gray-200 hover:border-deepBlue text-gray-600 hover:text-deepBlue rounded-xl font-bold text-center transition-all duration-300"
                      >
                        Log in to existing account
                      </Link>
                    </div>

                    <div className="text-center text-xs text-gray-400 mt-4 leading-relaxed">
                      By continuing, you agree to our{" "}
                      <Link to="/terms" className="text-visaro-orange hover:underline">Terms of Service</Link> &{" "}
                      <Link to="/privacy" className="text-visaro-orange hover:underline">Privacy Policy</Link>
                    </div>
                  </Form>
                )}
              </Formik>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SelectAccount;
